import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { CornerLeftDown, CornerRightUp } from "lucide-react";
import { useTranslation, Trans } from "react-i18next";

const PWA_INSTALL_MODAL_KEY = "byggve_pwa_install_shown";

// Detect device type and OS
const detectDevice = () => {
  const userAgent = navigator.userAgent;

  const isMobile = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(
    userAgent.toLowerCase(),
  );

  const isIOS = /iphone|ipad|ipod/i.test(userAgent.toLowerCase());
  const isAndroid = /android/i.test(userAgent.toLowerCase());

  // Detect Chrome on iOS (CriOS in user agent)
  const isChrome = /crios/i.test(userAgent.toLowerCase());
  // Safari is iOS but not Chrome (and not other browsers like Firefox)
  const isSafari = isIOS && !isChrome && !/fxios|edgios|opios/i.test(userAgent.toLowerCase());

  return { isMobile, isIOS, isAndroid, isChrome, isSafari };
};

// Calculate device info once at module load
const deviceInfo = detectDevice();

export const PWAInstallModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, isLoading } = useAuth();
  const { isMobile, isIOS, isAndroid, isChrome, isSafari } = deviceInfo;
  const { t } = useTranslation();

  useEffect(() => {
    // Only show modal if:
    // 1. User is authenticated
    // 2. Not loading
    // 3. On mobile device
    // 4. On iOS, only show for Chrome or Safari
    // 5. Modal hasn't been shown before
    const shouldShowModal = user && !isLoading && isMobile && (!isIOS || isChrome || isSafari);

    if (shouldShowModal) {
      try {
        const hasShown = localStorage.getItem(PWA_INSTALL_MODAL_KEY);
        if (!hasShown) {
          setIsOpen(true);
        }
      } catch {
        // If localStorage fails (private browsing, quota exceeded, etc.),
        // show modal anyway - better UX than crashing
        setIsOpen(true);
      }
    }
  }, [user, isLoading, isMobile, isIOS, isChrome, isSafari]);

  const handleClose = () => {
    try {
      localStorage.setItem(PWA_INSTALL_MODAL_KEY, "true");
    } catch {
      // Silently fail - user can close modal, just won't persist
    }
    setIsOpen(false);
  };

  // Don't render if not on mobile
  if (!isMobile) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent
        className={`sm:max-w-md fixed translate-y-0 ${
          isAndroid
            ? "top-0 bottom-auto pb-2 pt-16 data-[state=open]:slide-in-from-top-0"
            : "bottom-0 top-auto pt-12 data-[state=open]:slide-in-from-bottom-0"
        }`}
      >
        <DialogHeader>
          <DialogTitle>{t("landingPage.pwaInstall.title")}</DialogTitle>
          <DialogDescription>{t("landingPage.pwaInstall.description")}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {isIOS && (
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">{t("landingPage.pwaInstall.iosTitle")}</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>
                  {isChrome ? (
                    <>
                      <Trans
                        i18nKey="landingPage.pwaInstall.iosStep1Chrome"
                        components={{ strong: <strong /> }}
                      />{" "}
                      <span className="inline-block">
                        <svg className="inline w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z" />
                        </svg>
                      </span>
                    </>
                  ) : (
                    <>
                      <Trans
                        i18nKey="landingPage.pwaInstall.iosStep1"
                        components={{ strong: <strong /> }}
                      />{" "}
                      <span className="inline-block">
                        <svg className="inline w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M16 5l-1.42 1.42-1.59-1.59V16h-1.98V4.83L9.42 6.42 8 5l4-4 4 4zm4 5v11c0 1.1-.9 2-2 2H6c-1.11 0-2-.9-2-2V10c0-1.11.89-2 2-2h3v2H6v11h12V10h-3V8h3c1.1 0 2 .89 2 2z" />
                        </svg>
                      </span>
                    </>
                  )}
                </li>
                <li>
                  <Trans
                    i18nKey="landingPage.pwaInstall.iosStep2"
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    i18nKey="landingPage.pwaInstall.iosStep3"
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>{t("landingPage.pwaInstall.iosStep4")}</li>
              </ol>
            </div>
          )}

          {isAndroid && (
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">{t("landingPage.pwaInstall.androidTitle")}</h3>
              <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                <li>
                  <Trans
                    i18nKey="landingPage.pwaInstall.androidStep1"
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    i18nKey="landingPage.pwaInstall.androidStep2"
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    i18nKey="landingPage.pwaInstall.androidStep3"
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>{t("landingPage.pwaInstall.androidStep4")}</li>
              </ol>
            </div>
          )}

          {!isIOS && !isAndroid && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {t("landingPage.pwaInstall.genericInstructions")}
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="pb-12">
          <Button onClick={handleClose} className="w-full">
            {t("landingPage.pwaInstall.gotIt")}
          </Button>
        </DialogFooter>

        {/* Arrow pointing down - Safari on iOS only (not Chrome) */}
        {isSafari && (
          <div className="absolute bottom-2 left-0 right-0 flex flex-col items-center">
            <CornerLeftDown className="w-12 h-12 text-primary pb-2" strokeWidth={3} />
          </div>
        )}

        {/* Arrow pointing up-right - Android only */}
        {isAndroid && (
          <div className="absolute top-2 right-2 flex flex-col items-end">
            <CornerRightUp className="w-12 h-12 text-primary pt-2" strokeWidth={3} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
